import React, { ChangeEvent, FunctionComponent, useState } from 'react';
import InputMask from '@mona-health/react-input-mask';
import { useTheme } from '@mui/material/styles';

import {
  InputAdornment,
  TextField
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

var he = require('he');

type BankAccountDutchProps = {
  sx?: object;
  label: string;
  value: string;
  fullWidth?: boolean;
  error?: boolean;
  id: string;
  type?: string;
  errorArray?: string[];
  onChange: (z:string, y:boolean) => void;
};

// Dutch IBAN input. Mirrors `BankAccount` (Belgian) but with an NL mask
// (NL99 AAAA 9999 9999 99, 18 chars) ported from the mobile app's
// `ZichtAccountDutch.tsx`. Same mod-97 checksum, same `onChange(value, checked)` signature.
export const BankAccountDutch: FunctionComponent<BankAccountDutchProps> = ({label, onChange, sx, error, value, id, type, fullWidth, errorArray}) => {
  const theme = useTheme();

  const [okay, setOkay] = useState<boolean>(false);
  const [realValue, setRealValue] = useState<string>('');

  const checkIban = (raw: string): boolean => {
    // Dutch IBANs are exactly 18 chars: NL + 2 check digits + 4 bank code + 10 account.
    let iban = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (iban.length !== 18) return false;

    let code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    if (!code || !code[1] || !code[2] || !code[3]) return false;

    let digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter) {
      return (letter.charCodeAt(0) - 55).toString();
    });
    let checksum: number = parseInt(digits.slice(0, 2), 10);
    let fragment: string;
    for (let offset = 2; offset < digits.length; offset += 7) {
      fragment = String(checksum) + digits.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum === 1;
  };

  if (!realValue && !!value) {
    setOkay(checkIban(value));
    setRealValue(value);
  }

  return (
    <InputMask
      mask={[/[A-Z]/i, /[A-Z]/i, /[0-9]/, /[0-9]/, " ", /[A-Z]/i, /[A-Z]/i, /[A-Z]/i, /[A-Z]/i, " ", /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, " ", /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, " ", /[0-9]/, /[0-9]/]}
      value={realValue}
      onChange={(v: ChangeEvent<HTMLInputElement>) => {
        let checked = checkIban(v.target.value);
        setOkay(checked);
        setRealValue(v.target.value);
        if (!!onChange) onChange(v.target.value, checked);
      }}
    >
      <TextField
        id={id}
        error={!!errorArray && errorArray.indexOf(id) > -1}
        type={type}
        variant="outlined"
        label={he.decode(label)}
        sx={{
          ...(fullWidth ? {
            width: '100%',
            paddingTop: 1,
            paddingBottom: 1
          } : {
            width: 'auto',
            padding: 1
          }),
          'input': {
            padding: 1
          },
          'label[data-shrink=true]': {
            left: fullWidth ? 0 : 8,
            top: 8
          },
          'label[data-shrink=false]': {
            opacity: 0.4
          },
          ...sx
        }}
        InputProps={{
          endAdornment: (<InputAdornment position="end">
            {!okay && <CloseIcon sx={{color: theme.palette.secondary.main}} />}
            {!!okay && <CheckIcon sx={{color: theme.palette.tertiary?.main}} />}
          </InputAdornment>)
        }}
      />
    </InputMask>
  );
}
